/* eslint-disable camelcase */
import { Language } from '@nx-smartmonkey/shared/domain';
import countries, { DEFAULT_COUNTRY_CODE } from './Countries';

interface OptimizerConfigOption {
  label: string;
  value: string;
}

export interface OptimizerConfigMap {
  id: string;
  type: `minutes` | `number` | `boolean` | `select` | `country-autocomplete` | `slider` | `boolean_span`;
  noTextId?: string;
  options?: Array<OptimizerConfigOption>;
  max?: number;
  min?: number;
  step?: number;
  description?: string;
}

export const FIRST_SOLUTION_STRATEGY: Array<OptimizerConfigOption> = [
  { value: `AUTOMATIC`, label: `AUTOMATIC` },
  { value: `PATH_CHEAPEST_ARC`, label: `PATH_CHEAPEST_ARC` },
  { value: `PATH_MOST_CONSTRAINED_ARC`, label: `PATH_MOST_CONSTRAINED_ARC` },
  { value: `EVALUATOR_STRATEGY`, label: `EVALUATOR_STRATEGY` },
  { value: `SAVINGS`, label: `SAVINGS` },
  { value: `SWEEP`, label: `SWEEP` },
  { value: `CHRISTOFIDES`, label: `CHRISTOFIDES` },
  { value: `ALL_UNPERFORMED`, label: `ALL_UNPERFORMED` },
  { value: `BEST_INSERTION`, label: `BEST_INSERTION` },
  {
    value: `PARALLEL_CHEAPEST_INSERTION`,
    label: `PARALLEL_CHEAPEST_INSERTION (default)`,
  },
  { value: `LOCAL_CHEAPEST_INSERTION`, label: `LOCAL_CHEAPEST_INSERTION` },
  { value: `GLOBAL_CHEAPEST_ARC`, label: `GLOBAL_CHEAPEST_ARC` },
  { value: `LOCAL_CHEAPEST_ARC`, label: `LOCAL_CHEAPEST_ARC` },
  { value: `FIRST_UNBOUND_MIN_VALUE`, label: `FIRST_UNBOUND_MIN_VALUE` },
];

export const LOCAL_SEARCH_STRATEGY: Array<OptimizerConfigOption> = [
  { value: `AUTOMATIC`, label: `AUTOMATIC (default)` },
  { value: `GREEDY_DESCENT`, label: `GREEDY_DESCENT` },
  { value: `GUIDED_LOCAL_SEARCH`, label: `GUIDED_LOCAL_SEARCH` },
  { value: `SIMULATED_ANNEALING`, label: `SIMULATED_ANNEALING` },
  { value: `TABU_SEARCH`, label: `TABU_SEARCH` },
];

// Map used for optimizer config by a user with root or admin roles of Routal.
export const getOptimizerConfigSmRootMap = (): Array<OptimizerConfigMap> => {
  return [
    {
      id: `first_solution_strategy`,
      type: `select`,
      options: [...FIRST_SOLUTION_STRATEGY],
    },
    {
      id: `local_search_strategy`,
      type: `select`,
      options: [...LOCAL_SEARCH_STRATEGY],
    },
    { id: `time_limit_seconds`, type: `minutes`, max: 3600, min: 0 },
    { id: `lns_time_limit`, type: `minutes` },
    // { id: `max_wait_time`, type: `minutes`, description: `max_wait_time.description` },
    // { id: `service_duration`, type: `minutes` },
    // { id: `balance_services`, type: `boolean`, description: `balance_services.description` },
    { id: `time_span_coefficient`, type: `number`, description: `time_span_coefficient.description` },
    { id: `distance_span_coefficient`, type: `number`, description: `distance_span_coefficient.description` },
    { id: `use_distance`, type: `boolean`, description: `use_distance.description` },
    { id: `drop_penalty`, type: `number`, description: `drop_penalty.description` },
    { id: `distance_balance`, type: `number`, description: `distance_balance.description` },
    { id: `isolation_factor`, type: `number`, description: `isolation_factor.description` },
    // {
    //   id: `operation_country`,
    //   type: `country-autocomplete`,
    //   noTextId: `none`,
    //   options: parseCountries(countries),
    // },
  ];
};

export const getOptimizerConfigMap = (language?: Language): Array<OptimizerConfigMap> => {
  return [
    {
      id: `operation_country`,
      type: `country-autocomplete`,
      noTextId: `none`,
      options: parseCountries(countries, language),
    },
    { id: `distance_balance`, type: `slider`, description: `distance_balance.description`, min: 0.1, max: 4, step: 1 },
    {
      id: `isolation_factor`,
      type: `slider`,
      description: `isolation_factor.description`,
      min: 1,
      max: 2.5,
      step: 0.5,
    },
    { id: `max_wait_time`, type: `minutes`, description: `max_wait_time.description` },
    { id: `service_duration`, type: `minutes` },
    { id: `balance_services`, type: `boolean`, description: `balance_services.description` },
    // { id: `time_span_coefficient`, type: `minutes`, description: `time_span_coefficient.description` },
    // { id: `distance_span_coefficient`, type: `number`, description: `distance_span_coefficient.description` },
    // { id: `use_distance`, type: `boolean`, description: `use_distance.description` },
    // { id: `drop_penalty`, type: `number`, description: `drop_penalty.description` },
    { id: `time_span_coefficient`, type: `boolean_span`, description: `time_span_coefficient.description` },
    { id: `distance_span_coefficient`, type: `boolean_span`, description: `distance_span_coefficient.description` },
  ];
};

/**
 * Creates an array from a countries object. Also order the content by country label.
 * @param {*} countriesObject
 */
export function parseCountries(countriesObject: any, language?: Language) {
  const parsedLang = language?.value.toUpperCase() ?? DEFAULT_COUNTRY_CODE;

  const countriesArray = Object.keys(countriesObject).map((countryKey) => ({
    value: countryKey,
    label: countries[countryKey].label[parsedLang],
  }));

  return countriesArray.sort((a, b) => {
    if (a.label < b.label) return -1;
    if (a.label > b.label) return 1;
    return 0;
  });
}
